import { getEpaFuelEfficiencyRatings } from "./generated/calls/epa-fuel-efficiency-ratings/epa-fuel-efficiency-ratings";
import type { EpaFuelEfficiencyRatings as EpaFuelEfficiencyRatingsDTO } from "./generated/models";
import { buildHeaders } from "./helpers/headerHelper";
import { postgrestError } from "./helpers/postgrestErrorHelper";
import { postgrestFilter } from "./helpers/queryHelper";
import type { APIResult } from "./types";

export type { EpaFuelEfficiencyRatingsDTO };

/**
 * Fetches the EPA fuel efficiency ratings for the given ids.
 *
 * `epa_fuel_efficiency_ratings` is ~51k rows with no server-side pagination,
 * so it must never be listed unfiltered - that would attempt to return the
 * whole table and crash the server. Every call here must go through this
 * function's `id in (...)` filter, never `getEpaFuelEfficiencyRatings`
 * directly.
 */
async function fetchEpaFuelEfficiencyRatingsByIds(
  epaFuelEfficiencyIds: string[],
): Promise<APIResult<EpaFuelEfficiencyRatingsDTO[]>> {
  // Guard: an empty id list must never reach the API. `postgrestFilter([])`
  // serialises to `in.()`, and there's no reason to trust every PostgREST
  // version/proxy in front of this endpoint treats an empty `in` list as
  // "match nothing" rather than "no filter" - the latter would fetch the
  // entire unpaginated table, exactly what this function exists to prevent.
  if (epaFuelEfficiencyIds.length === 0) {
    return { success: true, data: [] };
  }

  try {
    const headers = buildHeaders();
    const res = await getEpaFuelEfficiencyRatings(
      { id: postgrestFilter(epaFuelEfficiencyIds) },
      { headers },
    );

    switch (res.status) {
      // A read narrowed by `limit`, `offset` or a `Range` header answers 206
      // and carries the same rows as a 200.
      case 200:
      case 206:
        return {
          success: true,
          data: res.data,
        };
      default:
        throw postgrestError("Fetch failed", res);
    }
  } catch (error) {
    return {
      success: false,
      errorMsg:
        error instanceof Error ? error.message : "an unknown error occurred",
    };
  }
}

export { fetchEpaFuelEfficiencyRatingsByIds };
